'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { BarChart3, Download, Layers, Plus, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { usePromotionsList } from '@/lib/api/promotions';
import { PromotionsTable } from './components/PromotionsTable';
import { BulkCreateDiscountModal } from './components/bulk-create-discount-modal';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { TableCard } from '@/components/ui/table-card';
import { useTablePage } from '@/hooks/use-table-page';
import {
  promotionsFilterMapping,
  promotionsSortMapping,
  promotionsQueryParamsMapping,
} from './utils/table-mapping';

const DiscountsPage = () => {
  const { storeRoutes, navigate, id } = useStoreNavigate();
  const { t } = useTranslation(['stores', 'common']);
  const [bulkCreateOpen, setBulkCreateOpen] = React.useState(false);


  const { setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: promotionsFilterMapping,
    sortMapping: promotionsSortMapping,
    queryParamsMapping: promotionsQueryParamsMapping,
  });


  const {
    data: promotionsResponse,
    isLoading: loading,
    error,
    refetch,
    isRefetching
  } = usePromotionsList({
    storeId: id,
    pagination: {
      ...queryInput.pagination,
      page: queryInput.pagination.page - 1,
    },
    filters: queryInput.filters,
    sortBy: queryInput.sortBy,
  });

  const promotions = promotionsResponse?.items || [];
  const total = promotionsResponse?.total || 0;

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">{t('stores:discountsAndPromotions')}</h1>
          <p className="text-muted-foreground text-sm">{t('stores:manageDiscountsDescription')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => navigate(storeRoutes.PROMOTIONS_ANALYTICS())}
          >
            <BarChart3 className="size-4" />
            {t('stores:promotionAnalytics')}
          </Button>
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => setBulkCreateOpen(true)}>
            <Layers className="size-4" />
            {t('stores:bulkCreateDiscount')}
          </Button>
          <Button className="w-full sm:w-auto" onClick={() => navigate(storeRoutes.DISCOUNT_CREATE())}>
            <Plus className="size-4" />
            {t('stores:addDiscount')}
          </Button>
        </div>
      </div>
      <TableCard>
        <PromotionsTable
          promotions={promotions}
          total={total}
          loading={loading || isRefetching}
          onTableStateChange={setTableState}
          onRefetch={refetch}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('stores:loadDiscountsError')}
          </div>
        )}
      </TableCard>

      <BulkCreateDiscountModal
        open={bulkCreateOpen}
        onOpenChange={setBulkCreateOpen}
        storeId={id}
      />
    </div>
  );
};

const DiscountsPageGuarded = withRequiredPermission(DiscountsPage, PERMISSIONS.STORES_VIEW);
export default DiscountsPageGuarded;
export { DiscountsPageGuarded as DiscountsPage };
